import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, Briefcase, FileText, BookOpen, GraduationCap, Inbox, BarChart3, ClipboardList } from "lucide-react";
import { UpcomingEventsCard } from "@/components/EventsWidgets";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="kb-mono text-xs text-kb-ink-muted">Loading admin overview…</div>;

  const tiles = [
    { k: data.users_total, l: "Total Users", icon: Users, to: "/admin/users" },
    { k: data.pending_mentor_requests, l: "Pending Requests", icon: Inbox, to: "/admin/mentor-requests" },
    { k: data.ideas_total, l: "Active Projects", icon: Briefcase, to: "/admin/projects" },
    { k: data.papers_total, l: "Published Papers", icon: FileText, to: "/admin/papers" },
    { k: data.journals_total, l: "Journals", icon: BookOpen },
    { k: data.courses_total, l: "Courses", icon: GraduationCap },
    { k: data.surveys_total, l: "Surveys", icon: ClipboardList },
    { k: data.mentors_total, l: "Mentors", icon: Users },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADMIN CONTROL ROOM</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Platform <em className="italic text-kb-primary">overview</em></h1>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
        {tiles.map((t, i) => {
          const Inner = (
            <>
              <div className="flex items-start justify-between">
                <t.icon className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
                <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">METRIC</span>
              </div>
              <div className="font-mono text-3xl text-kb-primary mt-4">{t.k}</div>
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">{t.l}</div>
            </>
          );
          return t.to ? (
            <Link to={t.to} key={i} className="bg-white p-6 hover:bg-kb-subtle transition-colors" data-testid={`admin-tile-${t.l.toLowerCase().replace(/\s+/g, '-')}`}>{Inner}</Link>
          ) : (
            <div key={i} className="bg-white p-6" data-testid={`admin-tile-${t.l.toLowerCase().replace(/\s+/g, '-')}`}>{Inner}</div>
          );
        })}
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <div className="kb-card p-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">USERS BY ROLE</div>
          <ul className="mt-4 space-y-2.5">
            {Object.entries(data.users_by_role).map(([r, n]) => (
              <li key={r} className="flex items-center gap-3 text-sm">
                <span className="text-kb-ink capitalize w-44">{r.replace(/_/g, " ")}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-kb-primary" style={{ width: `${Math.min(100, n * 10)}%` }} />
                </div>
                <span className="kb-mono text-xs text-kb-ink-muted w-8 text-right">{n}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="kb-card p-6">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">QUICK ACTIONS</div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Link to="/admin/mentor-requests" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary"><Inbox className="w-4 h-4 text-kb-primary" /><div className="text-sm text-kb-ink mt-2 font-medium">Review requests</div></Link>
            <Link to="/admin/users" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary"><Users className="w-4 h-4 text-kb-primary" /><div className="text-sm text-kb-ink mt-2 font-medium">Manage users</div></Link>
            <Link to="/admin/papers" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary"><FileText className="w-4 h-4 text-kb-primary" /><div className="text-sm text-kb-ink mt-2 font-medium">Curate papers</div></Link>
            <Link to="/admin/analytics" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary"><BarChart3 className="w-4 h-4 text-kb-primary" /><div className="text-sm text-kb-ink mt-2 font-medium">View analytics</div></Link>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <UpcomingEventsCard title="Upcoming events" />
      </div>
    </div>
  );
}
